import * as React from 'react';
import { useEffect, useContext } from 'react';
import { ISourceConfig } from '../models';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { AxiosResponse } from 'axios';
import { useQueryClient } from 'react-query';
import { apigeeEndpoint } from '../apis';
import { PaginatedRetrieves, Retrieve, RetrieveDbServiceResponse, RetrieveFlightResponse } from '@tff/types/TFF';

export type RetrieveDbHookProps = {
  sourceConfig?: ISourceConfig;
  recordLocator?: string;
  tfmPnr?: string;
  clientPnr?: string;
  product?: string;
  pageSize?: number;
  cursor?: string;
};

export type TypeOfStatus = 'IN_FETCHING' | 'FETCHED' | 'FAILURE';

export type RetrieveDbHookResponse = {
  fetchedRetrieveDbItems?: PaginatedRetrieves | Retrieve[];
  fetchedRetrieveDbItem?: Retrieve;
  fetchedRetrieveDbStatus?: TypeOfStatus;
  fetchedRetrieveDbError?: any;
  resetRetrieveDbState: () => void;
};

export const useRetrieveDbService = (retrieveBookingProps?: RetrieveDbHookProps): RetrieveDbHookResponse => {
  const [fetchedRetrieveDbStatus, setFetchedRetrieveDbStatus] = React.useState<TypeOfStatus | undefined>(undefined);
  const [fetchedRetrieveDbItem, setFetchedRetrieveDbItem] = React.useState<Retrieve | undefined>(undefined);
  const [fetchedRetrieveDbItems, setFetchedRetrieveDbItems] = React.useState<
    PaginatedRetrieves | Retrieve[] | undefined
  >({
    items: [],
    total: 0,
  });
  const [fetchedRetrieveDbError, setFetchedRetrieveDbError] = React.useState(undefined);
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const queryClient = useQueryClient();

  const resetRetrieveDbState = (): void => {
    setFetchedRetrieveDbItem(undefined);
    setFetchedRetrieveDbError(undefined);
    setFetchedRetrieveDbStatus(undefined);
    setFetchedRetrieveDbItems({ items: [], total: 0 });
  };

  useEffect(() => {
    const fetchDataFromBackend = async (
      recordLocator?: string,
      sourceConfig?: ISourceConfig,
      tfmPnr?: string,
      clientPnr?: string,
      product?: string,
      pageSize?: number,
      cursor?: string,
    ): Promise<void> => {
      try {
        recordLocator
          ? showLoading(`Search item at retrieve DB, RecordLocator: ${recordLocator}`)
          : showLoading(`Search items at retrieve DB`);
        setFetchedRetrieveDbStatus('IN_FETCHING');
        let retrieveDbResponse: RetrieveDbServiceResponse | undefined;
        let response: AxiosResponse<RetrieveDbServiceResponse> | undefined;
        if (sourceConfig) {
          response = await queryClient.fetchQuery(
            `get:retrieveDbItem:recordLocator:${recordLocator}`,
            async () => {
              return await apigeeEndpoint.get<RetrieveFlightResponse>(
                `/retrieve-db/${sourceConfig.SourceName}/${recordLocator}`,
              );
            },
            {
              retry: 2,
              cacheTime: 10000,
              initialData: () => {
                return queryClient.getQueryData(`get:retrieveDbItem:recordLocator:${recordLocator}`);
              },
            },
          );
          retrieveDbResponse = response?.data;
        } else if (clientPnr || recordLocator || product) {
          const criterion = clientPnr || recordLocator || product;

          let key =
            recordLocator === 'ALL' ? 'get:retrieveDbItem:LIST' : `post:retrieveDbItem:recordLocator:${criterion}`;

          const searchCriteria: any = {};
          if (clientPnr) {
            searchCriteria.clientPnr = clientPnr;
            key = `post:retrieveDbItem:clientPnr:${clientPnr}`;
          } else if (recordLocator && recordLocator !== 'ALL') {
            searchCriteria.recordLocator = recordLocator;
          }
          if (product) {
            searchCriteria.product = product;
            searchCriteria.cursor = cursor;
            searchCriteria.limit = pageSize;
            key += `:product:${product}:cursor:${cursor}:limit:${pageSize}`;
          }

          response = await queryClient.fetchQuery(
            key,
            async () => {
              return recordLocator === 'ALL'
                ? await apigeeEndpoint.get<Retrieve[]>(`/retrieve-db`)
                : await apigeeEndpoint.post<Retrieve[]>(`/retrieve-db`, searchCriteria);
            },
            {
              retry: 2,
              staleTime: recordLocator === 'ALL' ? 100000 : 60000,
              cacheTime: recordLocator === 'ALL' ? 100000 : 60000,
              initialData: () => {
                return queryClient.getQueryData(key);
              },
            },
          );

          if (recordLocator === 'ALL' || (!recordLocator && !clientPnr && product)) {
            retrieveDbResponse = response.data;
            if ('items' in retrieveDbResponse)
              retrieveDbResponse.items.sort((a: Retrieve, b: Retrieve) =>
                a.createdAt && b.createdAt ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : 0,
              );
            else if (Array.isArray(retrieveDbResponse))
              retrieveDbResponse.sort((a: Retrieve, b: Retrieve) =>
                a.createdAt && b.createdAt ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : 0,
              );
          } else if (recordLocator || clientPnr) {
            retrieveDbResponse = response.data;
            if ((retrieveDbResponse as Retrieve[]).length > 1) {
              throw new Error('MULTIPLE_RECORDLOCATORS_FOUND');
            } else {
              retrieveDbResponse = response.data;
            }
          }
        } else if (tfmPnr) {
          // search only by recordLocator
          response = await queryClient.fetchQuery(
            `get:retrieveDbItem:tfmPnr:${recordLocator}`,
            async () => await apigeeEndpoint.get<Retrieve>(`/retrieve-db/${tfmPnr}`),
            {
              retry: 2,
              staleTime: 1.0,
              cacheTime: 1000,
              initialData: () => {
                return queryClient.getQueryData(`get:retrieveDbItem:tfmPnr:${recordLocator}`);
              },
            },
          );
          retrieveDbResponse = response.data;
        }

        if (response?.data && 'errorMessage' in response?.data) {
          throw response?.data['errorMessage'];
        }

        if (clientPnr || tfmPnr || (recordLocator && recordLocator !== 'ALL')) {
          if (Array.isArray(retrieveDbResponse)) {
            setFetchedRetrieveDbItem(retrieveDbResponse[0]);
          } else {
            setFetchedRetrieveDbItem(retrieveDbResponse as Retrieve);
          }
        } else setFetchedRetrieveDbItems(retrieveDbResponse as PaginatedRetrieves);
        setFetchedRetrieveDbStatus('FETCHED');
      } catch (error) {
        setFetchedRetrieveDbError(error);
        setFetchedRetrieveDbStatus('FAILURE');
      } finally {
        closeLoading();
      }
    };

    if (
      (retrieveBookingProps?.product ||
        retrieveBookingProps?.recordLocator ||
        retrieveBookingProps?.tfmPnr ||
        retrieveBookingProps?.clientPnr) &&
      !fetchedRetrieveDbStatus
    ) {
      void fetchDataFromBackend(
        retrieveBookingProps.recordLocator,
        retrieveBookingProps.sourceConfig,
        retrieveBookingProps.tfmPnr,
        retrieveBookingProps.clientPnr,
        retrieveBookingProps.product,
        retrieveBookingProps.pageSize,
        retrieveBookingProps.cursor,
      );
    }
  }, [retrieveBookingProps]);

  return {
    fetchedRetrieveDbItems,
    fetchedRetrieveDbStatus,
    fetchedRetrieveDbItem,
    fetchedRetrieveDbError,
    resetRetrieveDbState,
  };
};
